import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import ErrorScreen from '../components/ErrorScreen';
import { ApiError } from '../lib/api';

export default function RouteError() {
  const error = useRouteError();
  const navigate = useNavigate();

  const missing = isRouteErrorResponse(error) ? error.status === 404 : error instanceof ApiError && error.status === 404;
  const text = error instanceof Error ? `${error.message}\n${error.stack || ''}` : '';

  const staleBuild =
    !isRouteErrorResponse(error) &&
    /unable to preload css|failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module/i.test(text);

  const apiDown =
    !staleBuild &&
    !isRouteErrorResponse(error) &&
    error instanceof Error &&
    (/Request failed \(50[0234]\)/i.test(error.message) || /failed to fetch|ECONNREFUSED|proxy/i.test(text));

  const serverDown =
    !staleBuild &&
    !apiDown &&
    error instanceof Error &&
    /fetch|network|offline|failed to load|service unavailable|gateway|timeout/i.test(error.message);

  const title = missing
    ? 'This page is not on the slate'
    : isRouteErrorResponse(error)
      ? `${error.status} ${error.statusText}`
      : staleBuild
        ? 'The studio was updated'
        : apiDown
          ? 'API is not ready'
          : serverDown
            ? 'Server unavailable'
            : 'Frameon could not open';

  const code = missing
    ? 'HTTP 404'
    : isRouteErrorResponse(error)
      ? `HTTP ${error.status}`
      : staleBuild
        ? 'STALE BUILD'
        : apiDown
          ? 'API :8000'
          : serverDown
            ? 'NETWORK'
            : 'RUNTIME';

  const detail = staleBuild
    ? 'A newer build shipped while this tab was open, so part of this page moved. Retrying loads the new build — your productions, shots and takes are untouched.'
    : apiDown
      ? 'The web app is up, but it could not reach the API on port 8000. Leave the “Frameon API” window open until it prints the server URL, then retry. If that window shows an error, that is the real failure — not this page.'
      : missing || isRouteErrorResponse(error)
        ? 'Nothing is filed at this address — it may have been moved or deleted. Head back to your productions and pick it up from there.'
        : 'Something failed while opening this page. Nothing was spent and nothing was lost — retrying usually clears it.';

  const action = missing
    ? { label: 'Back to productions', busyLabel: 'Opening…', icon: 'back' as const, onClick: () => navigate('/') }
    : { label: 'Retry', busyLabel: 'Reloading…', onClick: () => navigate(0) };

  return <ErrorScreen title={title} code={code} detail={detail} action={action} />;
}
